import React, { useEffect, useRef, useState } from 'react'
import { FeedbackType, ParaphraseFeedback } from '@chegg-wt/core-wa/types'
import { useDispatch, useSelector } from 'react-redux'
import {
  isPaperCheckInProgressSelector,
  paperIdSelector,
  sentenceOffsetsSelector,
} from '@chegg-wt/core-wa/state/selectors'
import {
  ErrorData,
  ProofReaderCardActions,
  SentenceOffset,
  ToastType,
} from '@chegg-wt/core-wa/state/types'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { ThreeDots } from '@chegg-wt/core-wa/components/ThreeDots'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { componentOrigins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import {
  CLARITY_CARD_REASON_TEST_ID,
  CLARITY_CARD_REASON_TITLE,
  CLARITY_CARD_TEST_ID,
} from '@chegg-wt/core-wa/constants/paraphraser'

import { Accordion } from '@chegg-wt/core-wa/components/Accordion'
import history from '@chegg-wt/core-wa/utils/history/history'
import { HistoryDeltaAction } from '@chegg-wt/core-wa/utils/history/types'
import { notifyToast, setFeedbackReason } from '@chegg-wt/core-wa/state/actions'
import {
  NoThanksCTA,
  SuggestionCardText,
  SuggestionCardTitle,
  SuggestionCardWrapper,
} from '@chegg-wt/core-wa/components/SuggestionCard'
import { DiffHighlight } from '../../utils'
import { ClarityCardProps } from '../../type'
import {
  ButtonsContainer,
  ClarityReason,
  ShimmerReasonCard,
  UpdateButton,
} from './styled'
import { handleClarityReasonFeedback } from './utils/handleClarityReasonFeedback'

const getParaphraseDescription = (
  feedback: ParaphraseFeedback,
  sentenceOffsets: SentenceOffset[]
) => {
  const { originalText } = sentenceOffsets.find(
    (sentence) => sentence.id === feedback.sentenceId
  )
  const { suggestedText } = feedback
  return <DiffHighlight originalText={originalText} suggestedText={suggestedText} />
}

const ClarityCard: React.FC<ClarityCardProps> = ({
  feedback,
  isExpanded,
  handleCardToggle,
  handleDeleteClick,
  setFocus,
  hasError = null,
}) => {
  const isPaperCheckInProgress = useSelector(isPaperCheckInProgressSelector)
  const sentenceOffsets = useSelector(sentenceOffsetsSelector)
  const paperId = useSelector(paperIdSelector)

  const cardRef = useRef(null)
  const description = getParaphraseDescription(feedback, sentenceOffsets)
  const dispatch = useDispatch()

  const [isReasonDisplay, setIsReasonDisplay] = useState(false)
  const [error, setError] = useState<ErrorData>(hasError)

  const {
    impactedText: originalText,
    suggestedText: correctedSentence,
    sentenceId,
    id: clarityCardId,
  } = feedback

  const handleClarityFeedbackReasonData = (
    feedbackReason: string,
    clarityCardId: string
  ) => {
    dispatch(setFeedbackReason(feedbackReason, clarityCardId))
  }

  const onClarityReasonFeedback = (isAccordionExpanded: boolean) => {
    setIsReasonDisplay(isAccordionExpanded)
    dispatch(
      userClick({
        origin: isAccordionExpanded
          ? origins.expandClarityReasonClick
          : origins.contractClarityReasonClick,
        text: originalText,
        value: correctedSentence,
        id: clarityCardId,
      })
    )

    if (!isAccordionExpanded || feedback.reason) return

    handleClarityReasonFeedback({
      originalText,
      correctedSentence,
      paperId,
      sentenceId,
      clarityCardId,
      handleClarityFeedbackReasonData,
      setError,
    })
  }

  const suggestionApplyHandler = () => {
    // Add apply card to history
    history.addFeedbackChange({
      feedback,
      action: HistoryDeltaAction.APPLY,
    })

    const editor = getFeatureEditor()
    const { impactedText, suggestedText, id } = feedback
    dispatch(
      userClick({
        origin: origins.proofReaderFeedbackCardClick,
        text: impactedText,
        value: suggestedText,
        id,
        citationMetadata: {
          proofReaderCardtype: FeedbackType.PARAPHRASE,
          proofReaderCardAction: ProofReaderCardActions.ACCEPT,
        },
      })
    )
    const impactedSentence = sentenceOffsets.find(
      (sentenceOffset) => sentenceOffset.id === feedback.sentenceId
    )

    // update sentence in editor and add sentence span
    editor
      .chain()
      .replaceCustomUnderlineText(
        {
          id: impactedSentence.id,
          offset: impactedSentence.offset,
          length: impactedSentence.length,
          nonManualUpdateType: FeedbackType.PARAPHRASE,
        },
        feedback.suggestedText
      )
      .run()

    setFocus()
  }

  useEffect(() => {
    if (feedback) {
      const { impactedText, suggestedText, id } = feedback
      dispatch(
        emitComponentViewEvent({
          origin: componentOrigins.proofReaderFeedbackCard,
          text: impactedText,
          value: suggestedText,
          id,
          metadata: { proofReaderCardtype: FeedbackType.PARAPHRASE },
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isExpanded) setIsReasonDisplay(false)

    if (isExpanded && cardRef.current) {
      if (!cardRef.current.parentElement.scrollTo) return
      cardRef.current.parentElement.scrollTo({
        top: cardRef.current.offsetTop,
        behavior: 'smooth',
      })
    }
  }, [isExpanded])

  useEffect(() => {
    if (error) {
      setIsReasonDisplay(false)
      dispatch(notifyToast(ToastType.CLARITY_REASON_ERROR))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleThreeDotsClick = () => {
    dispatch(notifyToast(ToastType.FLAG))

    const { impactedText, suggestedText, id } = feedback
    dispatch(
      userClick({
        origin: origins.proofReaderFeedbackCardClick,
        text: impactedText,
        value: suggestedText,
        id,
        citationMetadata: {
          proofReaderCardtype: FeedbackType.PARAPHRASE,
          proofReaderCardAction: ProofReaderCardActions.CLICK,
        },
      })
    )
    handleDeleteClick(true)
  }
  return (
    <SuggestionCardWrapper
      ref={cardRef}
      onClick={handleCardToggle}
      isExpanded={isExpanded}
    >
      <SuggestionCardTitle isExpanded={isExpanded}>
        {!isExpanded && (
          <SuggestionCardText isTruncated>{description}</SuggestionCardText>
        )}
      </SuggestionCardTitle>

      {isExpanded && (
        <>
          <SuggestionCardText isExpanded={isExpanded}>{description}</SuggestionCardText>
          <Accordion
            dataTestId={CLARITY_CARD_REASON_TEST_ID}
            title={CLARITY_CARD_REASON_TITLE}
            isExpanded={isReasonDisplay}
            setIsExpanded={onClarityReasonFeedback}
          >
            {feedback.reason ? (
              <ClarityReason>{feedback.reason}</ClarityReason>
            ) : (
              <ShimmerReasonCard />
            )}
          </Accordion>
          <ButtonsContainer>
            <UpdateButton
              disabled={isPaperCheckInProgress}
              onClick={suggestionApplyHandler}
              data-test={`${CLARITY_CARD_TEST_ID}-update-button`}
            >
              Update
            </UpdateButton>
            <NoThanksCTA
              data-test={`${CLARITY_CARD_TEST_ID}-no-thanks-button`}
              disabled={isPaperCheckInProgress}
              onClick={() => handleDeleteClick(false)}
            >
              No thanks
            </NoThanksCTA>
            <ThreeDots
              disabled={isPaperCheckInProgress}
              onFlagClick={handleThreeDotsClick}
              position="topRight"
              data-test={`${CLARITY_CARD_TEST_ID}-three-dots-button`}
            />
          </ButtonsContainer>
        </>
      )}
    </SuggestionCardWrapper>
  )
}

export default ClarityCard
